import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import type { ReactNode } from "react";
import { useUser } from "@clerk/react";

import { identify, pageview, track } from "./lib/analytics.js";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { Home } from "./pages/Home.js";
import { Login } from "./pages/Login.js";
import { SignUp } from "./pages/SignUp.js";
import { GuestUpload } from "./pages/GuestUpload.js";
import { EventPublic } from "./pages/EventPublic.js";
import { Wall } from "./pages/Wall.js";
import { NotFound } from "./pages/NotFound.js";
import { PageLoader } from "../../components/Skeleton.js";
import { DEFAULT_TITLE, pageMetaFor } from "../../src/lib/seo.js";
import { COMPARISONS } from "../../src/lib/compare.js";
import { GUIDES } from "../../src/lib/guides.js";

const Dashboard = lazy(() => import("./pages/Dashboard.js").then((m) => ({ default: m.Dashboard })));
const CreateEvent = lazy(() => import("./pages/CreateEvent.js").then((m) => ({ default: m.CreateEvent })));
const EventControl = lazy(() => import("./pages/EventControl.js").then((m) => ({ default: m.EventControl })));
const Billing = lazy(() => import("./pages/Billing.js").then((m) => ({ default: m.Billing })));
const Pricing = lazy(() => import("./pages/Pricing.js").then((m) => ({ default: m.Pricing })));
const Demo = lazy(() => import("./pages/Demo.js").then((m) => ({ default: m.Demo })));
const Privacy = lazy(() => import("./pages/Privacy.js").then((m) => ({ default: m.Privacy })));
const Guide = lazy(() => import("./pages/Guide.js").then((m) => ({ default: m.Guide })));
const Compare = lazy(() => import("./pages/Compare.js").then((m) => ({ default: m.Compare })));
const Terms = lazy(() => import("./pages/Terms.js").then((m) => ({ default: m.Terms })));
const PrintQrCards = lazy(
  () => import("./pages/PrintQrCards.js").then((m) => ({ default: m.PrintQrCards })),
);
const JoinWorkspace = lazy(
  () => import("./pages/JoinWorkspace.js").then((m) => ({ default: m.JoinWorkspace })),
);

/**
 * Gate for the host surface.
 *
 * `useAuth().loading` stays true until Clerk has finished rehydrating *and* the
 * Worker has answered /api/auth/me, so redirecting on it cannot bounce a
 * signed-in host to /login on a slow connection. Redirecting on `!host` alone
 * would.
 */
function RequireHost({ children }: { children: ReactNode }) {
  const { host, loading, clerkUnavailable, hostError, refresh } = useAuth();

  /*
   * Checked before `loading`, because `loading` has now given up by this point:
   * Clerk was still not loaded after the deadline, so `loading` is false while
   * `clerkUnavailable` is true. Without this ordering the host would fall
   * through to the redirect below and be told to sign in — which is wrong, and
   * is precisely the confusion the deadline exists to remove.
   */
  if (clerkUnavailable) {
    return (
      <div className="layout stack" style={{ maxWidth: "30rem", textAlign: "center", paddingTop: "3rem" }}>
        <h1 style={{ marginBottom: "0.25rem" }}>Could not reach sign-in</h1>
        <p className="muted">
          This page needs to check who you are, and the sign-in service could not be
          loaded. That is usually the network rather than you — a guest wifi
          portal, a proxy, or a content blocker that stops scripts.
        </p>
        <div>
          <button className="button" type="button" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <PageLoader label="Loading your account" />;
  }

  if (hostError) {
    return (
      <div className="layout stack" style={{ maxWidth: "32rem", textAlign: "center", paddingTop: "3rem" }}>
        <h1 style={{ marginBottom: "0.25rem" }}>Could not load your account</h1>
        <p className="muted">{hostError}</p>
        <p className="muted">
          This is usually temporary. Your events and uploads are not affected.
        </p>
        <div>
          <button className="button" type="button" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!host) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Routes.
 *
 * The three guest surfaces are separate paths on purpose, because they are used
 * by three different kinds of device held by three different kinds of person:
 *
 *   /u/:guestCode   a guest on a phone, who has just scanned a table card
 *   /e/:slug        a guest browsing, or anyone sent the gallery link
 *   /wall/:slug     a television, with no keyboard and no session
 *
 * The host surface is /manage/:slug rather than /e/:slug, so a shared gallery
 * link can never be a route into somebody else's dashboard.
 */
function Shell() {
  // Keep the tab title and description in step with the page. The Worker writes
  // the same values into the served HTML (src/lib/prerender.ts); this covers
  // in-app navigation, which never asks the Worker for a new document.
  const { pathname } = useLocation();
  const { isSignedIn, user } = useUser();

  // Track pageviews on every route change.
  useEffect(() => {
    pageview(pathname);
  }, [pathname]);

  // Identify the user in PostHog as soon as Clerk knows who they are.
  useEffect(() => {
    if (isSignedIn && user?.id) {
      identify(user.id, {
        email: user.emailAddresses[0]?.emailAddress ?? undefined,
        first_name: user.firstName ?? undefined,
        last_name: user.lastName ?? undefined,
      });
      track("user_signed_in");
    }
  }, [isSignedIn, user]);

  useEffect(() => {
    const meta = pageMetaFor(pathname);
    document.title = meta?.title ?? DEFAULT_TITLE;
    if (meta) document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
  }, [pathname]);

  return (
    <Suspense
      fallback={<PageLoader />}
    >
      <Routes>
      <Route path="/" element={<Home />} />
      {/* Public rate card. Billing stays behind RequireHost because it shows
          the signed-in host's own storage and ledger. */}
      <Route path="/pricing" element={<Pricing />} />
      {/* Static sample album. Public, no database row and no upload path. */}
      <Route path="/demo" element={<Demo />} />
      {/* Trust & compliance legal pages */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      {COMPARISONS.map((c) => (
        <Route key={c.slug} path={`/${c.slug}`} element={<Compare />} />
      ))}
      {GUIDES.map((g) => (
        <Route key={g.slug} path={`/${g.slug}`} element={<Guide />} />
      ))}
      {/* Both are splats: Clerk's path routing puts the second factor, SSO
          callback and password reset on sub-paths of these URLs. A plain
          "/login" route answers the first step and 404s the second. */}
      <Route path="/login/*" element={<Login />} />
      <Route path="/sign-up/*" element={<SignUp />} />
      {/* The Account Portal's old spelling, kept working for any link already
          shared during the beta. */}
      <Route path="/signup" element={<Navigate to="/sign-up" replace />} />
      <Route
        path="/dashboard"
        element={
          <RequireHost>
            <Dashboard />
          </RequireHost>
        }
      />
      <Route
        path="/events/new"
        element={
          <RequireHost>
            <CreateEvent />
          </RequireHost>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireHost>
            <Billing />
          </RequireHost>
        }
      />
      <Route
        path="/manage/:slug"
        element={
          <RequireHost>
            <EventControl />
          </RequireHost>
        }
      />
      <Route
        path="/print/:slug"
        element={
          <RequireHost>
            <PrintQrCards />
          </RequireHost>
        }
      />
      {/* The same sheet under the event it belongs to; /print/:slug stays for
          links already shared from the Share panel. */}
      <Route
        path="/manage/:slug/cards"
        element={
          <RequireHost>
            <PrintQrCards />
          </RequireHost>
        }
      />
      <Route path="/u/:guestCode" element={<GuestUpload />} />
      <Route path="/e/:slug" element={<EventPublic />} />
      <Route path="/wall/:slug" element={<Wall />} />
      {/* The invitation's landing page: reachable signed-out, because "sign in
          then return" is the flow, and the token survives that round trip. */}
      <Route path="/join/:token" element={<JoinWorkspace />} />
      {/* The old host path, kept working so links shared during a beta still land somewhere sensible. */}
      <Route path="/event/:slug" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
